import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiGetCached } from "@/lib/api";
import SoldOutCard from "@/components/SoldOutCard";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon, Search, MapPin, KeyRound, Sparkles, CreditCard, Wallet, Banknote, Gauge } from "lucide-react";
import { format, differenceInDays, addDays } from "date-fns";
import { it } from "date-fns/locale";

const CATS = [
  { v: "all", l: "Qualsiasi categoria" },
  { v: "sportiva", l: "Sportive" },
  { v: "supercar", l: "Supercar" },
  { v: "suv", l: "SUV" },
  { v: "berlina", l: "Berline" },
  { v: "citycar", l: "City Car" },
  { v: "monovolume", l: "Monovolume" },
  { v: "crossover", l: "Crossover" },
  { v: "surprise", l: "🎁 Sorprendimi" },
];

const SURPRISE_POOL = ["sportiva", "supercar", "suv", "berlina", "citycar", "monovolume", "crossover"];

// Category ranking from cheapest/simplest → premium; used to suggest upgrades in results
const CATEGORY_TIER = {
  citycar: 1,
  crossover: 2,
  berlina: 2,
  monovolume: 3,
  suv: 4,
  sportiva: 5,
  supercar: 6,
};

const rateForMethodFn = (car, method, rentalType) => {
  if (rentalType === "weekly") return Number(car.rent_weekly || car.rent_daily_credit || car.rent_daily_cash || car.rent_daily || 0);
  if (rentalType === "monthly") return Number(car.rent_monthly || car.rent_weekly || car.rent_daily_credit || car.rent_daily_cash || car.rent_daily || 0);
  // Daily: prefer method-specific rate; otherwise fall back to any defined rate so the car is still bookable.
  if (method === "credit" && car.rent_daily_credit) return Number(car.rent_daily_credit);
  if (method === "prepaid" && car.rent_daily_prepaid) return Number(car.rent_daily_prepaid);
  if (method === "cash" && car.rent_daily_cash) return Number(car.rent_daily_cash);
  return Number(car.rent_daily_credit || car.rent_daily_cash || car.rent_daily_prepaid || car.rent_daily || 0);
};

// Finds the cheapest higher-tier car in the same results set, delta ≤ €50/day
function findUpgrade(current, results, payMethod, rentalType) {
  const currentTier = CATEGORY_TIER[current.category] || 0;
  const currentRate = rateForMethodFn(current, payMethod, rentalType);
  if (!currentTier || !currentRate) return null;
  const candidates = results
    .filter((c) => c.id !== current.id)
    .filter((c) => (CATEGORY_TIER[c.category] || 0) > currentTier)
    .map((c) => ({ car: c, rate: rateForMethodFn(c, payMethod, rentalType) }))
    .filter((x) => x.rate > currentRate)
    .map((x) => ({ ...x, delta: Math.round(x.rate - currentRate) }))
    .filter((x) => x.delta > 0 && x.delta <= 80)
    .sort((a, b) => a.delta - b.delta);
  return candidates[0] || null;
}

const AGES = [
  { v: "18", l: "18 anni" },
  { v: "19", l: "19 anni" },
  { v: "20", l: "20 anni" },
  { v: "21", l: "21 anni" },
  { v: "22", l: "22 anni" },
  { v: "23", l: "23 anni" },
  { v: "24", l: "24 anni" },
  { v: "25", l: "25 anni" },
  { v: "26+", l: "26+ anni" },
];

const COUNTRIES = [
  "Italia", "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua e Barbuda", "Arabia Saudita",
  "Argentina", "Armenia", "Australia", "Austria", "Azerbaigian", "Bahamas", "Bahrein", "Bangladesh",
  "Barbados", "Belgio", "Belize", "Benin", "Bhutan", "Bielorussia", "Birmania", "Bolivia",
  "Bosnia ed Erzegovina", "Botswana", "Brasile", "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
  "Cambogia", "Camerun", "Canada", "Capo Verde", "Ciad", "Cile", "Cina", "Cipro", "Città del Vaticano",
  "Colombia", "Comore", "Corea del Nord", "Corea del Sud", "Costa d'Avorio", "Costa Rica", "Croazia",
  "Cuba", "Danimarca", "Dominica", "Ecuador", "Egitto", "El Salvador", "Emirati Arabi Uniti", "Eritrea",
  "Estonia", "Eswatini", "Etiopia", "Figi", "Filippine", "Finlandia", "Francia", "Gabon", "Gambia",
  "Georgia", "Germania", "Ghana", "Giamaica", "Giappone", "Gibuti", "Giordania", "Grecia", "Grenada",
  "Guatemala", "Guinea", "Guinea Equatoriale", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "India",
  "Indonesia", "Iran", "Iraq", "Irlanda", "Islanda", "Isole Marshall", "Isole Salomone", "Israele",
  "Kazakistan", "Kenya", "Kirghizistan", "Kiribati", "Kosovo", "Kuwait", "Laos", "Lesotho", "Lettonia",
  "Libano", "Liberia", "Libia", "Liechtenstein", "Lituania", "Lussemburgo", "Macedonia del Nord",
  "Madagascar", "Malawi", "Malaysia", "Maldive", "Mali", "Malta", "Marocco", "Mauritania", "Mauritius",
  "Messico", "Micronesia", "Moldavia", "Monaco", "Mongolia", "Montenegro", "Mozambico", "Namibia",
  "Nauru", "Nepal", "Nicaragua", "Niger", "Nigeria", "Norvegia", "Nuova Zelanda", "Oman", "Paesi Bassi",
  "Pakistan", "Palau", "Palestina", "Panama", "Papua Nuova Guinea", "Paraguay", "Perù", "Polonia",
  "Portogallo", "Qatar", "Regno Unito", "Repubblica Ceca", "Repubblica Centrafricana",
  "Repubblica del Congo", "Repubblica Democratica del Congo", "Repubblica Dominicana", "Romania",
  "Ruanda", "Russia", "Saint Kitts e Nevis", "Saint Lucia", "Saint Vincent e Grenadine", "Samoa",
  "San Marino", "São Tomé e Príncipe", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore",
  "Siria", "Slovacchia", "Slovenia", "Somalia", "Spagna", "Sri Lanka", "Stati Uniti d'America", "Sudafrica",
  "Sudan", "Sudan del Sud", "Suriname", "Svezia", "Svizzera", "Tagikistan", "Taiwan", "Tanzania",
  "Thailandia", "Timor Est", "Togo", "Tonga", "Trinidad e Tobago", "Tunisia", "Turchia", "Turkmenistan",
  "Tuvalu", "Ucraina", "Uganda", "Ungheria", "Uruguay", "Uzbekistan", "Vanuatu", "Venezuela", "Vietnam",
  "Yemen", "Zambia", "Zimbabwe",
];

// Pickup/Dropoff time slots — Mon–Sat 09:30 → 18:00 at 30-min intervals.
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 9; h <= 18; h++) {
    for (const m of [0, 30]) {
      if (h === 9 && m === 0) continue; // start at 09:30
      if (h === 18 && m > 0) continue;   // stop at 18:00
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return slots;
})();

// `date-fns getDay` returns 0 = Sunday, 1 = Monday, ... 6 = Saturday
const isSunday = (d) => d && new Date(d).getDay() === 0;
const nextMondayFrom = (d) => {
  const nd = new Date(d);
  // Move forward until we hit Monday (day 1)
  while (nd.getDay() !== 1) nd.setDate(nd.getDate() + 1);
  return nd;
};


export default function RentalBookingBanner() {
  const nav = useNavigate();
  const [category, setCategory] = useState("all");
  const [pickup, setPickup] = useState(addDays(new Date(), 1));
  const [dropoff, setDropoff] = useState(addDays(new Date(), 3));
  // Opening hours: Mon–Sat 09:30 → 18:00. Sunday closed. Default = 10:00.
  const [pickupTime, setPickupTime] = useState("10:00");
  const [dropoffTime, setDropoffTime] = useState("10:00");
  const [age, setAge] = useState("26+");
  const [country, setCountry] = useState("Italia");
  const [discountCode, setDiscountCode] = useState("");
  const [results, setResults] = useState([]);
  const [soldOut, setSoldOut] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payMethod, setPayMethod] = useState(null);
  const [depositMethod, setDepositMethod] = useState("credit");  // credit | debit | cash
  const [surcharges, setSurcharges] = useState({ credit: 0, debit: 30, cash: 100 });
  const [surpriseOpen, setSurpriseOpen] = useState(false);
  const [surpriseWon, setSurpriseWon] = useState(null); // { category, code }
  const [kmPacks, setKmPacks] = useState([]); // Admin-editable chips: [{id, label}]
  const [kmOption, setKmOption] = useState("base"); // selected chip id

  // Prefetch the rental fleet once: drives the category list (only categories with cars) and makes search instant
  const [fleet, setFleet] = useState(null);
  useEffect(() => {
    apiGetCached("/cars", { ttlMs: 10_000, params: { listing_type: "rent", light: true } })
      .then((d) => setFleet(Array.isArray(d) ? d : []))
      .catch(() => setFleet([]));
  }, []);
  const carCats = (c) => ((c.categories || []).length ? c.categories : [c.category]).filter(Boolean);
  const fleetCats = new Set((fleet || []).filter((c) => c.available).flatMap(carCats));
  const visibleCats = CATS.filter((c) =>
    c.v === "all" || !fleet || fleetCats.has(c.v) || (c.v === "surprise" && SURPRISE_POOL.some((p) => fleetCats.has(p)))
  );

  // Load deposit surcharges once
  useEffect(() => {
    api.get("/public/deposit-info").then((r) => {
      if (r.data?.surcharges) setSurcharges(r.data.surcharges);
    }).catch(() => {});
  }, []);

  // Load km package chip labels from admin-configurable pricing rules
  useEffect(() => {
    apiGetCached("/public/pricing-info", { ttlMs: 300_000 }).then((d) => {
      const list = d?.km_packages || [];
      if (Array.isArray(list) && list.length) setKmPacks(list);
    }).catch(() => {});
  }, []);

  // --- Weekend / closed-office logic ---
  // Office hours: Mon–Sat 09:30–18:00 — SUNDAY CLOSED.
  // If the user picks Sunday for dropoff, auto-roll it to the following Monday
  // and surface a transparent banner that explains why.
  const pickupIsSunday = isSunday(pickup);
  const dropoffIsSunday = isSunday(dropoff);
  const showClosedBanner = pickupIsSunday || dropoffIsSunday;

  useEffect(() => {
    if (dropoffIsSunday) {
      const monday = nextMondayFrom(dropoff);
      setDropoff(monday);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropoffIsSunday]);

  // --- Short-rental day count with pickup/dropoff times ---
  // Each day = 24h from pickup. Any return beyond the 24h window of the LAST
  // day adds +1 day. Example: pickup Mon 10:00, dropoff Tue 11:00 = 2 days.
  const [hh1, mm1] = pickupTime.split(":").map(Number);
  const [hh2, mm2] = dropoffTime.split(":").map(Number);
  const pickupMs = new Date(pickup).setHours(hh1, mm1, 0, 0);
  const dropoffMs = new Date(dropoff).setHours(hh2, mm2, 0, 0);
  const hoursDiff = Math.max(1, (dropoffMs - pickupMs) / 3600000);
  const days = Math.max(1, Math.ceil(hoursDiff / 24));
  const rentalType = days <= 6 ? "daily" : days <= 27 ? "weekly" : "monthly";

  // Open the surprise gift box whenever the user picks "surprise" category
  const onCategoryChange = (v) => {
    if (v === "surprise") {
      // Pick a random pool category for the actual search but keep the dropdown showing "surprise".
      const pool = fleet ? SURPRISE_POOL.filter((p) => fleetCats.has(p)) : SURPRISE_POOL;
      const pick = (pool.length ? pool : SURPRISE_POOL)[Math.floor(Math.random() * (pool.length || SURPRISE_POOL.length))];
      setSurpriseWon({ category: pick, code: "SORPRESA10" });
      setSurpriseOpen(true);
      setCategory("surprise");
    } else {
      setSurpriseWon(null);
      setCategory(v);
    }
  };

  const applySurprise = () => {
    if (surpriseWon) {
      setCategory(surpriseWon.category);
      try { navigator.clipboard?.writeText(surpriseWon.code); } catch {}
    }
    setSurpriseOpen(false);
  };

  const openPayDialog = () => {
    setPayDialogOpen(true);
  };

  const rateForMethod = (c, method) => {
    if (rentalType === "weekly") return c.rent_weekly || c.rent_daily_credit || c.rent_daily_cash || c.rent_daily;
    if (rentalType === "monthly") return c.rent_monthly || c.rent_weekly || c.rent_daily_credit || c.rent_daily_cash || c.rent_daily;
    const map = {
      credit: c.rent_daily_credit,
      prepaid: c.rent_daily_prepaid,
      cash: c.rent_daily_cash,
    };
    // Prefer method-specific rate; otherwise fall back to any defined rate so the car still appears.
    return (method && map[method]) || c.rent_daily_credit || c.rent_daily_cash || c.rent_daily_prepaid || c.rent_daily || null;
  };

  const acceptsMethod = (c, method) => {
    // Default TRUE if the flag is not defined on the car (backward compatibility for old records)
    if (method === "credit") return c.accepts_credit !== false;
    if (method === "prepaid") return c.accepts_prepaid !== false;
    if (method === "cash") return c.accepts_cash !== false;
    return true;
  };

  const runSearch = async (method) => {
    setPayMethod(method);
    setPayDialogOpen(false);
    setLoading(true);
    setSearched(true);
    try {
      const list = fleet || (await apiGetCached("/cars", { ttlMs: 10_000, params: { listing_type: "rent", light: true } })) || [];
      const cars = list.filter((c) => {
        if (!c.available) return false;
        if (category !== "all" && category !== "surprise" && !carCats(c).includes(category)) return false;
        if (!acceptsMethod(c, method)) return false;
        const rate = rateForMethod(c, method);
        return !!rate;
      });
      // Single batch availability check instead of N+1 per-car calls
      let availableIds = new Set(cars.map((c) => c.id));
      try {
        const av = await api.post("/cars/availability-check", {
          car_ids: cars.map((c) => c.id),
          start_date: format(pickup, "yyyy-MM-dd"),
          end_date: format(dropoff, "yyyy-MM-dd"),
        });
        availableIds = new Set(av.data?.available || []);
      } catch { /* fall back to showing all */ }
      setResults(cars.filter((c) => availableIds.has(c.id)));
      setSoldOut(cars.filter((c) => !availableIds.has(c.id)));
    } finally {
      setLoading(false);
    }
  };

  const proceed = (carId) => {
    const qs = new URLSearchParams({
      pickup: format(pickup, "yyyy-MM-dd"),
      dropoff: format(dropoff, "yyyy-MM-dd"),
      pickup_time: pickupTime,
      dropoff_time: dropoffTime,
      rt: rentalType,
      age,
      country,
    });
    if (depositMethod) qs.set("dep", depositMethod);
    if (kmOption && kmOption !== "base") qs.set("km", kmOption);
    if (discountCode.trim()) qs.set("discount", discountCode.trim().toUpperCase());
    nav(`/auto/${carId}?${qs.toString()}`);
  };

  return (
    <section data-testid="rental-booking-banner" className="relative -mt-24 z-30 px-4 lg:px-12 mb-20">
      <div className="max-w-[1400px] mx-auto">
        {/* Outer glow wrapper */}
        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-[#22ff66] via-[#4EFF87] to-[#22ff66] opacity-40 blur-2xl animate-pulse" />
          <div className="relative bg-gradient-to-br from-[#1a1216] via-[#251820] to-[#1a1216] border-2 border-[#22ff66]/60 shadow-[0_40px_100px_-20px_rgba(34,255,102,0.6)] backdrop-blur-xl">
            {/* Header — bigger, more prominent */}
            <div className="px-6 lg:px-10 py-6 border-b border-white/10 flex items-center justify-between flex-wrap gap-3 bg-gradient-to-r from-[#22ff66]/25 via-[#22ff66]/10 to-transparent">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#22ff66] via-[#4EFF87] to-[#22ff66] p-[2px] shadow-[0_0_40px_rgba(34,255,102,0.7)]">
                  <div className="w-full h-full rounded-full bg-[#0e0e0e] flex items-center justify-center">
                    <KeyRound className="w-8 h-8 text-[#22ff66] -rotate-45" strokeWidth={2.2} />
                  </div>
                </div>
                <div>
                  <div className="heading-uc text-2xl md:text-3xl leading-tight">Prenota la tua auto</div>
                  <div className="text-xs uppercase tracking-widest text-white/60 mt-1">Verifica disponibilità in tempo reale</div>
                </div>
              </div>
            </div>

            {/* Closed-office banner — appears whenever pickup or dropoff lands on Sunday */}
            {showClosedBanner && (
              <div
                data-testid="closed-banner"
                className="mx-6 lg:mx-10 mt-4 mb-2 border-2 border-amber-400 bg-amber-400 px-4 py-3 text-black text-sm flex items-start gap-3 shadow-lg"
              >
                <span className="text-base leading-none">⚠️</span>
                <div className="flex-1">
                  <div className="font-bold uppercase tracking-widest text-[11px] mb-0.5 text-black">Ufficio chiuso la domenica</div>
                  <div className="text-xs leading-relaxed text-black/85">
                    Siamo aperti dal <b>lunedì al sabato, 09:30–18:00</b>.
                    {dropoffIsSunday && " La riconsegna è stata spostata automaticamente al lunedì successivo."}
                    {pickupIsSunday && " Scegli un giorno dal lunedì al sabato per il ritiro."}
                  </div>
                </div>
              </div>
            )}

            {/* Row 1: Category / Pickup / Dropoff */}
            <div className="px-6 lg:px-10 py-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
              <div className="md:col-span-2 lg:col-span-4">
                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Categoria</div>
                <Select value={category} onValueChange={onCategoryChange}>
                  <SelectTrigger data-testid="bb-category" className="bg-[#0e0e0e] border-white/20 rounded-none h-12 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0e0e0e] border-white/10 rounded-none">
                    {visibleCats.map((c) => (
                      <SelectItem key={c.v} value={c.v} className={c.v === "surprise" ? "text-[#22ff66] font-bold" : ""}>
                        {c.l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DateInput label="Data ritiro" date={pickup} setDate={setPickup} testid="bb-pickup" min={new Date()} colSpan="lg:col-span-4" />
              <DateInput label="Data riconsegna" date={dropoff} setDate={setDropoff} testid="bb-dropoff" min={pickup} colSpan="lg:col-span-4" />
            </div>

            {/* Row 1b: Pickup / Dropoff times — follows the opening-hours of the office (Mon–Sat 09:30–18:00) */}
            <div className="px-6 lg:px-10 pb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-4 lg:col-start-5">
                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Ora di ritiro</div>
                <Select value={pickupTime} onValueChange={setPickupTime}>
                  <SelectTrigger data-testid="bb-pickup-time" className="bg-[#0e0e0e] border-white/20 rounded-none h-12 text-white">
                    <SelectValue placeholder="Ora" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0e0e0e] border-white/10 rounded-none max-h-72">
                    {TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="lg:col-span-4">
                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Ora di riconsegna</div>
                <Select value={dropoffTime} onValueChange={setDropoffTime}>
                  <SelectTrigger data-testid="bb-dropoff-time" className="bg-[#0e0e0e] border-white/20 rounded-none h-12 text-white">
                    <SelectValue placeholder="Ora" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0e0e0e] border-white/10 rounded-none max-h-72">
                    {TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Age / Country / Discount code */}
            <div className="px-6 lg:px-10 pb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-4">
                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Età</div>
                <Select value={age} onValueChange={setAge}>
                  <SelectTrigger data-testid="bb-age" className="bg-[#0e0e0e] border-white/20 rounded-none h-12 text-white">
                    <SelectValue placeholder="Seleziona età" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0e0e0e] border-white/10 rounded-none max-h-72">
                    {AGES.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="lg:col-span-4">
                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Vivo in</div>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger data-testid="bb-country" className="bg-[#0e0e0e] border-white/20 rounded-none h-12 text-white">
                    <SelectValue placeholder="Seleziona paese" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0e0e0e] border-white/10 rounded-none max-h-72 overflow-y-auto">
                    {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2 lg:col-span-4">
                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-[#22ff66]" /> Codice sconto
                </div>
                <input
                  data-testid="bb-discount"
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  placeholder="Es. UTILE10"
                  className="w-full h-12 bg-[#0e0e0e] border border-white/20 hover:border-white/40 focus:border-[#22ff66] px-3 text-sm uppercase tracking-widest text-white placeholder:text-white/30 outline-none transition"
                />
              </div>
            </div>

            {/* Row 3: Km packages chip row (admin-editable) */}
            {kmPacks.length > 0 && (
              <div data-testid="km-chip-row" className="px-6 lg:px-10 pb-5">
                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1.5">
                  <Gauge className="w-3 h-3 text-[#22ff66]" /> Km inclusi
                </div>
                <div className="flex flex-wrap gap-2">
                  {kmPacks.map((p) => {
                    const active = kmOption === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        data-testid={`km-chip-${p.id}`}
                        onClick={() => setKmOption(p.id)}
                        className={`px-3.5 py-2 border text-[11px] uppercase tracking-widest font-semibold transition whitespace-nowrap rounded-full ${
                          active
                            ? "bg-[#22ff66] text-black border-[#22ff66] shadow-[0_0_20px_rgba(34,255,102,0.5)]"
                            : "bg-[#0e0e0e] text-white/70 border-white/20 hover:border-[#22ff66]/60 hover:text-white"
                        }`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Row 4: Verify button full-width */}
            <div className="px-6 lg:px-10 pb-6">
              <button
                data-testid="bb-verify"
                onClick={openPayDialog}
                disabled={loading}
                className="w-full h-14 bg-[#22ff66] hover:bg-[#4EFF87] disabled:opacity-80 text-black uppercase tracking-widest font-bold text-sm md:text-base flex items-center justify-center gap-3 btn-red-glow transition shadow-[0_10px_40px_-10px_rgba(34,255,102,0.8)] relative overflow-hidden rounded-full"
              >
                {loading ? (
                  <>
                    {/* Driving car — translates horizontally across the button */}
                    <span className="verify-car" aria-hidden>
                      <CarEmoji />
                    </span>
                    {/* Spinning magnifying glass */}
                    <span className="verify-lens inline-flex" aria-hidden>
                      <Search className="w-5 h-5" strokeWidth={2.4} />
                    </span>
                    <span className="verify-loading-text">Cercando la tua auto</span>
                    <span className="verify-dots"><span>.</span><span>.</span><span>.</span></span>
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" /> Verifica disponibilità
                  </>
                )}
              </button>
            </div>

            {/* Results */}
            {searched && (
              <div data-testid="bb-results" className="border-t border-white/10 px-6 lg:px-10 py-6">
                {loading ? (
                  <div className="text-center text-white/50 text-sm py-6">Verifica disponibilità...</div>
                ) : results.length === 0 && soldOut.length === 0 ? (
                  <div className="text-center text-white/50 text-sm py-6">
                    Nessuna vettura disponibile per i criteri selezionati.
                    <br />
                    <span className="text-[10px] uppercase tracking-widest text-white/30 mt-2 inline-block">
                      Prova a cambiare categoria o le date.
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                      <div className="text-[10px] uppercase tracking-widest text-[#22ff66] flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#22ff66] rounded-full animate-pulse" />
                        {results.length ? `${results.length} vetture disponibili` : "Tutte le vetture sono già prenotate per queste date"}
                      </div>
                      {payMethod && (
                        <div className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-2">
                          Pagamento:
                          <span className="text-[#22ff66] font-semibold flex items-center gap-1">
                            {payMethod === "credit" && (<><CreditCard className="w-3 h-3" /> Carta di credito</>)}
                            {payMethod === "prepaid" && (<><Wallet className="w-3 h-3" /> Carta prepagata</>)}
                            {payMethod === "cash" && (<><Banknote className="w-3 h-3" /> Contanti</>)}
                          </span>
                          <button onClick={openPayDialog} className="underline hover:text-[#22ff66]">Cambia</button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {results.map((c) => {
                        const rate = rateForMethod(c, payMethod);
                        const depFee = Number(surcharges[depositMethod] || 0);
                        const totalRate = rate + depFee;
                        const upgrade = findUpgrade(c, results, payMethod, rentalType);
                        return (
                          <div key={c.id} className="flex flex-col gap-2">
                            <button
                              data-testid={`bb-result-${c.id}`}
                              onClick={() => proceed(c.id)}
                              className="text-left bg-[#0e0e0e] border border-white/10 hover:border-[#22ff66] hover:shadow-[0_15px_40px_-15px_rgba(34,255,102,0.5)] transition-all duration-300 flex flex-col md:flex-row items-stretch group overflow-hidden min-h-[280px]"
                            >
                              <div className="relative w-full md:w-56 h-40 md:h-auto md:min-h-[260px] shrink-0 overflow-hidden bg-black">
                                <img
                                  src={c.images?.[0]}
                                  alt={`${c.brand} ${c.model}`}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                <div className="absolute top-2 left-2 flex gap-1.5">
                                  <span className="bg-black/70 backdrop-blur px-2 py-0.5 text-[9px] uppercase tracking-widest text-white">{c.category}</span>
                                </div>
                              </div>
                              <div className="flex-1 p-5 flex flex-col justify-between gap-3 min-w-0">
                                <div>
                                  <div className="text-[10px] uppercase tracking-widest text-[#22ff66] font-bold mb-1">{c.brand}</div>
                                  <div className="font-display font-bold text-lg leading-tight mb-2 truncate">{c.model}</div>
                                  <div className="flex flex-wrap gap-3 text-[10px] uppercase tracking-widest text-white/50">
                                    <span>{c.year}</span>
                                    <span>{c.fuel}</span>
                                    <span>{c.transmission}</span>
                                    {c.power_hp ? <span>{c.power_hp} CV</span> : null}
                                  </div>
                                </div>
                                <div className="flex items-end justify-between border-t border-white/10 pt-3">
                                  <div>
                                    <div className="text-[9px] uppercase tracking-widest text-white/40">Da</div>
                                    <div className="font-display font-black text-2xl text-white">
                                      €{totalRate}
                                      <span className="text-xs font-normal text-white/50 ml-1">
                                        / {rentalType === "daily" ? "giorno" : rentalType === "weekly" ? "sett." : "mese"}
                                      </span>
                                    </div>
                                    {depFee > 0 && (
                                      <div data-testid={`rate-breakdown-${c.id}`} className="text-[9px] text-white/40 mt-0.5">
                                        €{rate} + €{depFee} caparra ({depositMethod === "debit" ? "debito" : "contanti"})
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-[11px] uppercase tracking-widest font-semibold text-white/60 group-hover:text-[#22ff66] transition flex items-center gap-1.5 whitespace-nowrap">
                                    Prenota →
                                  </div>
                                </div>
                              </div>
                            </button>

                            {/* Upgrade upsell — next-tier car with smallest price delta */}
                            {upgrade && (
                              <button
                                data-testid={`bb-upgrade-${c.id}`}
                                onClick={() => proceed(upgrade.car.id)}
                                className="flex items-center gap-3 border border-[#22ff66]/40 bg-[#22ff66]/10 hover:bg-[#22ff66]/20 px-4 py-3 text-left transition group/upgrade"
                              >
                                <div className="w-9 h-9 bg-[#22ff66] text-black flex items-center justify-center shrink-0 font-black text-lg">▲</div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[10px] uppercase tracking-widest text-[#22ff66] font-bold">Esegui upgrade</div>
                                  <div className="text-xs text-white truncate">
                                    Con solo <b className="text-[#22ff66]">+€{upgrade.delta}/g</b> puoi avere una <b className="capitalize">{upgrade.car.category}</b> — {upgrade.car.brand} {upgrade.car.model}
                                  </div>
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-white/70 group-hover/upgrade:text-[#22ff66] transition whitespace-nowrap">
                                  Passa a →
                                </div>
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {soldOut.length > 0 && (
                      <div data-testid="bb-soldout-list" className={results.length ? "mt-8" : ""}>
                        <div className="text-[10px] uppercase tracking-widest text-[#ff3b3b] font-bold mb-4 flex items-center gap-2">
                          <span className="w-2 h-2 bg-[#ff3b3b] rounded-full" />
                          {soldOut.length === 1 ? "1 vettura già prenotata" : `${soldOut.length} vetture già prenotate`} per queste date
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          {soldOut.map((c) => <SoldOutCard key={c.id} car={c} />)}
                        </div>
                      </div>
                    )}

                    {/* Category vs Model guarantee note */}
                    <div data-testid="category-guarantee-note" className="mt-6 text-[11px] text-white/50 text-center px-4 leading-relaxed">
                      <span className="text-[#22ff66]">*</span> Viene garantita la <b className="text-white/70">categoria della vettura</b>, non il modello specifico. Il modello mostrato è indicativo e potrebbe essere sostituito con una vettura equivalente della stessa categoria.
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment method chooser dialog */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent data-testid="pay-method-dialog" className="bg-[#0a0a0a] border-2 border-[#22ff66]/50 rounded-none max-w-xl text-white">
          <DialogHeader>
            <DialogTitle className="heading-uc text-2xl">Come vuoi noleggiare?</DialogTitle>
            <DialogDescription className="text-white/60 text-sm">
              Scegli metodo di pagamento e metodo di cauzione. Il prezzo si aggiorna di conseguenza.
            </DialogDescription>
          </DialogHeader>

          {/* STEP 1: Payment method for rental */}
          <div className="mt-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#22ff66] font-bold mb-2">1. Metodo di pagamento noleggio</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: "credit", Icon: CreditCard, l: "Credito" },
                { k: "prepaid", Icon: Wallet, l: "Prepagata" },
                { k: "cash", Icon: Banknote, l: "Contanti" },
              ].map((p) => {
                const active = payMethod === p.k;
                return (
                  <button
                    key={p.k}
                    data-testid={`pay-${p.k}`}
                    onClick={() => setPayMethod(p.k)}
                    className={`flex flex-col items-center gap-1.5 p-3 border transition text-center ${active ? "border-[#22ff66] bg-[#22ff66]/15" : "border-white/15 hover:border-[#22ff66]/60 hover:bg-white/5"}`}
                  >
                    <p.Icon className={`w-5 h-5 ${active ? "text-[#22ff66]" : "text-white/60"}`} />
                    <span className={`text-[11px] uppercase tracking-widest font-semibold ${active ? "text-[#22ff66]" : "text-white/80"}`}>{p.l}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* STEP 2: Deposit method — can change the price */}
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#22ff66] font-bold mb-2">2. Metodo cauzione (deposito)</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: "credit", Icon: CreditCard, l: "Carta credito", sub: "Preautorizzazione" },
                { k: "debit", Icon: Wallet, l: "Carta debito", sub: "Addebito immediato" },
                { k: "cash", Icon: Banknote, l: "Contanti", sub: "In agenzia al ritiro" },
              ].map((d) => {
                const active = depositMethod === d.k;
                const amt = Number(surcharges[d.k] || 0);
                return (
                  <button
                    key={d.k}
                    data-testid={`deposit-${d.k}`}
                    onClick={() => setDepositMethod(d.k)}
                    className={`flex flex-col items-start gap-1 p-3 border transition text-left ${active ? "border-[#22ff66] bg-[#22ff66]/15" : "border-white/15 hover:border-[#22ff66]/60 hover:bg-white/5"}`}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <d.Icon className={`w-4 h-4 ${active ? "text-[#22ff66]" : "text-white/60"}`} />
                      <span className={`text-[10px] uppercase tracking-widest font-bold ${active ? "text-[#22ff66]" : "text-white/80"} truncate`}>{d.l}</span>
                    </div>
                    <div className="text-[10px] text-white/50 leading-tight">{d.sub}</div>
                    <div className={`text-[11px] font-black ${amt === 0 ? "text-[#22ff66]" : "text-white/80"} mt-0.5`}>
                      {amt === 0 ? "Nessun costo" : `+ €${amt}`}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* CTA */}
          <button
            data-testid="pay-confirm"
            disabled={!payMethod}
            onClick={() => runSearch(payMethod)}
            className="mt-5 w-full h-12 bg-[#22ff66] hover:bg-[#4EFF87] disabled:opacity-40 disabled:cursor-not-allowed text-black uppercase tracking-widest text-xs font-black transition rounded-full"
          >
            {payMethod ? "Verifica disponibilità →" : "Scegli il metodo di pagamento"}
          </button>
        </DialogContent>
      </Dialog>

      {/* 🎁 Sorprendimi — gift box popup with random category + 10% discount code */}
      {surpriseOpen && (
        <SurpriseGiftModal
          won={surpriseWon}
          onClose={() => setSurpriseOpen(false)}
          onApply={applySurprise}
        />
      )}
    </section>
  );
}

function SurpriseGiftModal({ won, onClose, onApply }) {
  const [opened, setOpened] = useState(false);
  const label = {
    sportiva: "Sportive",
    supercar: "Supercar",
    suv: "SUV",
    berlina: "Berline",
    citycar: "City Car",
    monovolume: "Monovolume",
  }[won?.category] || "Sorpresa";

  return (
    <div
      data-testid="surprise-gift-modal"
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-[#0e0e0e] border border-[#22ff66]/40 shadow-[0_0_80px_rgba(34,255,102,0.35)] p-8 text-center"
      >
        <button
          data-testid="surprise-close"
          onClick={onClose}
          aria-label="Chiudi"
          className="absolute right-3 top-3 w-9 h-9 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5"
        >
          ×
        </button>

        {!opened ? (
          <>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#22ff66] mb-4 font-bold">Un regalo per te</div>
            <h3 className="heading-uc text-3xl md:text-4xl mb-2 text-white">Scuoti il regalo!</h3>
            <p className="text-sm text-white/60 mb-8">Clicca sul fiocco per scoprire la tua categoria + bonus sconto.</p>

            <button
              data-testid="surprise-open"
              onClick={() => setOpened(true)}
              aria-label="Apri il regalo"
              className="mx-auto gift-wrap relative w-40 h-40 cursor-pointer group outline-none"
            >
              {/* Gift box body */}
              <div className="absolute inset-0 bg-[#22ff66] shadow-[0_30px_60px_-20px_rgba(34,255,102,0.6)] group-hover:translate-y-[-6px] transition-transform duration-300">
                {/* Vertical ribbon */}
                <div className="absolute inset-y-0 left-1/2 w-5 -translate-x-1/2 bg-white/90" />
                {/* Horizontal ribbon */}
                <div className="absolute inset-x-0 top-1/2 h-5 -translate-y-1/2 bg-white/90" />
                {/* Bow */}
                <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
                  <div className="w-8 h-8 bg-white rotate-45 shadow-md" />
                  <div className="w-8 h-8 bg-white -rotate-45 shadow-md -ml-4" />
                  <div className="absolute w-6 h-6 bg-[#22ff66] rounded-full border-2 border-white" />
                </div>
              </div>
              <div className="absolute inset-x-0 -bottom-8 text-[10px] uppercase tracking-widest text-white/50 group-hover:text-[#22ff66] transition">
                Clicca per scoprire →
              </div>
            </button>
          </>
        ) : (
          <>
            <div className="mb-5">
              <div className="mx-auto w-20 h-20 rounded-full bg-[#22ff66] text-black flex items-center justify-center text-4xl animate-in zoom-in duration-500">
                🎁
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#22ff66] mb-2 font-bold">Complimenti!</div>
            <h3 className="heading-uc text-3xl md:text-4xl mb-3 text-white animate-in slide-in-from-bottom-4 duration-500">La tua categoria</h3>
            <div
              data-testid="surprise-category"
              className="font-display font-black text-5xl mb-4 text-[#22ff66] animate-in zoom-in duration-500"
              style={{ textShadow: "0 0 40px rgba(34,255,102,0.6)" }}
            >
              {label}
            </div>
            <div className="my-6 border border-dashed border-[#22ff66]/60 bg-[#22ff66]/5 p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#22ff66] font-bold mb-1">+ Buono sconto 10%</div>
              <div
                data-testid="surprise-code"
                className="font-mono font-black text-2xl text-white tracking-widest"
              >
                {won?.code}
              </div>
              <div className="text-[10px] text-white/40 mt-1">Il codice sarà copiato negli appunti</div>
            </div>
            <button
              data-testid="surprise-apply"
              onClick={onApply}
              className="w-full h-12 bg-[#22ff66] hover:bg-[#4EFF87] text-black uppercase tracking-widest text-xs font-black transition flex items-center justify-center gap-2 rounded-full"
            >
              Vai alla ricerca
            </button>
            <div className="text-[10px] text-white/40 mt-3">
              Applica il codice <b className="text-white/70">{won?.code}</b> al checkout per usare lo sconto.
            </div>
          </>
        )}

        <style>{`
          .gift-wrap { animation: giftShake 1.6s ease-in-out infinite; transform-origin: 50% 100%; }
          @keyframes giftShake {
            0%, 100% { transform: rotate(-2deg); }
            25% { transform: rotate(2deg); }
            50% { transform: rotate(-3deg); }
            75% { transform: rotate(3deg); }
          }
          .gift-wrap:hover { animation-duration: 0.6s; }
          @media (prefers-reduced-motion: reduce) {
            .gift-wrap { animation: none !important; }
          }
        `}</style>
      </div>
    </div>
  );
}

function DateInput({ label, date, setDate, testid, min, colSpan = "md:col-span-3" }) {
  return (
    <div className={colSpan}>
      <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">{label}</div>
      <Popover>
        <PopoverTrigger
          data-testid={testid}
          className="w-full h-12 bg-[#0e0e0e] border border-white/20 hover:border-white/40 px-3 flex items-center gap-2 text-sm text-white transition"
        >
          <CalendarIcon className="w-4 h-4 text-[#22ff66]" />
          {date ? format(date, "EEE dd MMM yyyy", { locale: it }) : "Seleziona"}
        </PopoverTrigger>
        <PopoverContent className="bg-[#0e0e0e] border-white/10 rounded-none p-0">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => d && setDate(d)}
            disabled={(d) => {
              const t = new Date(); t.setHours(0,0,0,0);
              if (d < t) return true;
              if (min && d < min) return true;
              return false;
            }}
            locale={it}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Compact car silhouette used in the loading animation of the "Verifica disponibilità" button.
function CarEmoji() {
  return (
    <svg width="28" height="18" viewBox="0 0 48 24" fill="currentColor" aria-hidden>
      <path d="M6 18h2a3 3 0 0 0 6 0h20a3 3 0 0 0 6 0h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-1.4-1.9l-5.4-1.6-4.6-5.1A4 4 0 0 0 29.6 2H17a4 4 0 0 0-3.2 1.6L10 9H6a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2zm11-14h12.6a2 2 0 0 1 1.5.7L34.5 9H14l2.6-3.3A2 2 0 0 1 17 4z" />
      <circle cx="11" cy="18" r="2.6" fill="#0a0a0a" />
      <circle cx="37" cy="18" r="2.6" fill="#0a0a0a" />
    </svg>
  );
}

